import { getSelectedThemeName } from "../themeMeta";

/**
 * Homepage content, keyed by theme name.
 * Add more city themes by extending HOMEPAGE_CONTENT[themeName].
 */
export const HOMEPAGE_CONTENT = {
  default: {
    hero: {
      /**
       * Homepage hero copy + button labels.
       * Note: Background media is configured separately in `themeMeta.js` (homeHero).
       *
       * Inline bolding is supported using `**bold**` (preferred).
       * Legacy `[[bold]]` is also supported.
       */
      titleLines: [
        { text: "A decision brief for ", highlight: "operators" },
        { text: "in your city.", highlight: "" },
      ],
      subhead: [
        "High-signal, decision-ready context — not a link dump.",
        "Get the next brief in your inbox.",
      ],
      chevronAriaLabel: "Scroll to the next section",
      ctaLabel: "Find Out Now →",
    },
    socialProof: {
      stats: [
        { value: "2,400+", label: "Quizzes Taken" },
        { value: "4.9★", label: "Average Rating" },
      ],
    },
    whyThisMatters: {
      title: "Why [City] Is Different",
      subtitle: "3 Local Shifts",
      bullets: [
        {
          boldText: "Concentrated Startup Ecosystem",
          description:
            "AI is accelerating adoption of emerging tech here—faster than it seems from the outside.",
        },
        {
          boldText: "Unusual Talent Pool",
          description:
            "Our freelance and builder skilled acts as an early amplifier—local alignment matters here.",
        },
        {
          boldText: "Buyer Behavior Changes",
          description:
            "Early-stage startups and tech-based businesses are shifting budgets months ahead of job titles changing.",
        },
      ],
    },
    whatThisIsNot: {
      title: "What this is / what this is not",
      lead: "A briefing product for decision-makers. Scope is narrow and deliberate.",
      isBullets: [
        "A short intelligence brief on what matters in your city right now",
        "Written for action, not entertainment",
        "A clear point of view with supporting context",
        "One decision-ready takeaway per section",
      ],
      isNotBullets: [
        "A news recap or press-release rewrite",
        "A motivational newsletter",
        'A "here are 27 links" feed',
        "A partisan rant or culture-war content",
      ],
    },
    whatYouActuallyGet: {
      title: "What you actually get",
      lead: "Concrete outputs you can reference, forward, and use to make decisions.",
      bullets: [
        "A single brief with a clear thesis and supporting facts",
        "Named constraints and what they imply (budget, timing, stakeholders)",
        "Second-order effects: what changes if the thesis is true",
        "What to watch next (specific signals, not vague “stay informed”)",
        "A short “so what” section: what to do, defer, or ignore",
      ],
      // Examples:
      // Local businesses with unmet demand
      // Viable build ideas constrained by this city
      // Outreach scripts tailored to local buyers
      // Acquisition angles most people overlook
      // Price anchor appears here (even if no checkout).
      priceText: "$19.99",
      priceNote: undefined,
      cta: undefined,
      media: {
        // TODO: replace with CloudFront URL
        src: "",
        poster: undefined,
      },
    },
    whatThisExpectsOfYou: {
      title: "What this expects of you",
      lead: "This is not passive content. You are the operator.",
      steps: [
        {
          title: "Read → decide",
          text: "Read it once, then decide what changes (if anything) this week.",
          media: {
            src: "https://d2oa23hr7q6t96.cloudfront.net/playbook/tbbcheck.gif",
            alt: "Animated checklist illustration",
          },
        },
        {
          title: "Forward → act",
          text: "Forward it to the one person who can act on it (including yourself).",
          media: {
            src: "https://d2oa23hr7q6t96.cloudfront.net/playbook/tbbplan.gif",
            alt: "Animated planning illustration",
          },
        },
        {
          title: "Track → update",
          text: "Track the signals we cite and update your assumptions. Bring constraints — we won’t invent them for you.",
          media: {
            src: "https://d2oa23hr7q6t96.cloudfront.net/playbook/tbbdevices.gif",
            alt: "Animated devices illustration",
          },
        },
      ],
    },
    whoThisIsFor: {
      title: "If You Work in [City]",
      subtitle:
        "Your work is more exposed to silent shifts here—before they show up in headlines and job postings.",
      context:
        "If you live or work in [City], local incentives and buyer behavior shift before the story hits the news. This brief is for people who want to act on those shifts—without guessing.",
      bullets: [
        {
          text: "You want a clearer map of what changes locally before it hits headlines.",
        },
        { text: "You’d rather act on **signals** than guess based on noise." },
        { text: "You want decision-ready context you can use this week." },
      ],
      footer:
        "This brief doesn’t push you into a role. It helps you see the real tradeoffs—so you can choose a path with fewer blind spots.",
    },
    whatYouGet: {
      title: "What's Inside",
      intro:
        "A structured, decision-ready database of local opportunities and how to act on them.",
      deliverables: [
        { text: "What the opportunity is and who needs it in [City]" },
        { text: "How to find buyers (where to look + how to reach them)" },
        { text: "Pricing guidance and realistic ranges" },
        { text: "Templates you can use immediately" },
      ],
      exampleTitle: "Examples:",
      examples: [
        {
          name: "Fractional Ops Support",
          income: "$2k–$4k/mo (varies by scope)",
        },
        { name: "Local Lead Gen", income: "$500–$1,500/client/mo" },
      ],
      closing:
        "Organized by income level, startup cost, and time to first dollar.",
    },
    finalCta: {
      title: "See What Others Will Notice Late",
      subtitle:
        "Your work is more exposed to silent shifts here—before they show up in [City].",
      price: "$29",
      priceNote: "EARLY ACCESS · LIMITED TO THE FIRST GROUP IN [CITY]",
      cta: {
        label: "View the [City] Brief",
        to: "/sublanding",
      },
      note: undefined,
    },
    quizRequest: {
      quizSlug: "sanantonio_marketing_rep_v1",
      citySlug: "sanantonio",
      cityLabel: "San Antonio",
      persona: "marketing_rep",
    },
    interstitial: {
      header: "This Brief Isn't Public Yet",
      bodyParagraphs: [
        "The [City] Brief is being released to a small first group to validate demand before public launch.",
        "We’re confirming interest from people who would actually pay for this kind of local intelligence.",
      ],
      priceAnchor: "Expected price: $29",
      emailPlaceholder: "Email address",
      ctaLabel: "Get Access",
      variant: "email",
      // TODO: replace with Stripe Payment Link for this theme/city
      stripePaymentLink: "",
      microcopy:
        "No spam. No promises. This is a signal test, not a subscription.",
      successHeader: "You’re in the First Group",
      successBodyParagraphs: [
        "When access opens, you’ll be able to purchase the [City] Brief before it’s shared more widely.",
        "If demand is strong, this becomes a recurring local series.",
        "If not, it stops here.",
      ],
      // Backend route for this city/theme
      submitUrl: "/api/users/local-report-early",
    },
  },

  sanantonio: {
    quizRequest: {
      quizSlug: "sanantonio_marketing_rep_v1",
      citySlug: "sanantonio",
      cityLabel: "San Antonio",
      persona: "marketing_rep",
    },
    hero: {
      titleLines: [
        {
          text: "How AI-Proof Are You?",
          highlight: "",
        },
      ],
      subhead: [
        "Your role might be at risk—but are your skills?",
      ],
      chevronAriaLabel: "Scroll to see opportunities",
      ctaLabel: "Start Quiz →",
    },
    socialProof: {
      stats: [
        { value: "2,400+", label: "Quizzes Taken" },
        { value: "4.9★", label: "Average Rating" },
      ],
    },
    // NOTE: Sections like `whyThisMatters`, `whoThisIsFor`, `whatYouGet`,
    // `whatYouActuallyGet`, `finalCta`, and `whatThisIsNot` were removed —
    // QuizHomeScreen only renders hero + socialProof + (interstitial when
    // ?early=1). The defaults in this file's `default` block still provide
    // those keys for any future surface that needs them.
    interstitial: {
      header: "We're At Capacity This Month",
      bodyParagraphs: [
        "We're limiting our San Antonio launch to 50 people to ensure everyone gets quality support. This month's group is full. Next opening: February 20th.",
        "Reserve now for February 20th access and **lock in launch pricing of $19 with 1-year money-back guarantee.**",
        "",
      ],
      priceAnchor: "Early access price: $29.",
      emailPlaceholder: "Enter your email",
      ctaLabel: "Reserve My Spot for February 20th",
      variant: "email",
      stripePaymentLink: "https://buy.stripe.com/test_6oUaEY0B5345fysfHQdnW03",
      microcopy: "No payment now • Lock in $19 + 1-year guarantee",
      successHeader: "You're On The List!",
      successBodyParagraphs: [
        "You're locked in at **$19 with 1-year money-back guarantee**",
      ],
      submitUrl: "/api/users/local-report-early",
    },
  },
};

function mergeSection(base, theme) {
  const b = base || {};
  const t = theme || {};
  return { ...b, ...t };
}

/**
 * Returns the homepage content for the selected theme,
 * with defaults filled in so future city themes can override minimally.
 */
export function getHomepageContent(themeName = getSelectedThemeName()) {
  const base = HOMEPAGE_CONTENT.default || {};
  const theme = HOMEPAGE_CONTENT[themeName] || {};

  return {
    ...base,
    ...theme,
    hero: mergeSection(base.hero, theme.hero),
    whyThisMatters: mergeSection(base.whyThisMatters, theme.whyThisMatters),
    whatThisIsNot: mergeSection(base.whatThisIsNot, theme.whatThisIsNot),
    whatYouActuallyGet: {
      ...mergeSection(base.whatYouActuallyGet, theme.whatYouActuallyGet),
      media: {
        ...(base.whatYouActuallyGet?.media || {}),
        ...(theme.whatYouActuallyGet?.media || {}),
      },
      cta:
        theme.whatYouActuallyGet?.cta === undefined
          ? base.whatYouActuallyGet?.cta
          : theme.whatYouActuallyGet?.cta,
    },
    whatThisExpectsOfYou: mergeSection(
      base.whatThisExpectsOfYou,
      theme.whatThisExpectsOfYou,
    ),
    whoThisIsFor: mergeSection(base.whoThisIsFor, theme.whoThisIsFor),
    whatYouGet: mergeSection(base.whatYouGet, theme.whatYouGet),
    finalCta: {
      ...mergeSection(base.finalCta, theme.finalCta),
      cta: {
        ...(base.finalCta?.cta || {}),
        ...(theme.finalCta?.cta || {}),
      },
    },
    interstitial: mergeSection(base.interstitial, theme.interstitial),
    socialProof: mergeSection(base.socialProof, theme.socialProof),
    quizRequest: mergeSection(base.quizRequest, theme.quizRequest),
  };
}
